import {useCallback, useEffect, useState} from 'react';

import QRCode from 'qrcode';
import {useTranslation} from 'react-i18next';

import {parseNullableJson} from 'Shared/parseNullableJson';

/** The leader-visible summary of a listening group, as returned by the `/api/groups/*` routes. */
export interface GroupSummary {
    /** The group's ID. */
    groupId: string;
    /** The short, human-readable fallback code. */
    joinCode: string;
    /** ISO date string, after which the code/token are no longer valid. */
    joinCodeExpiresAt: string | null;
    /** The QR-code-ready join link. */
    joinUrl: string;
    /** The group's current status. */
    status: 'active' | 'ended';
}

/** A single connected (or previously connected) group member, from the leader's perspective. */
export interface GroupMemberInfo {
    /** Whether this member currently has an open SSE connection. */
    connected: boolean;
    /** The member's chosen display name. */
    displayName: string;
    /** The member's ID. */
    id: string;
}

/** The `/api/groups/[groupId]/status` response shape. */
interface GroupStatusResponse {
    /** Number of currently connected members. */
    memberCount: number;
    /** The connected members. */
    members: GroupMemberInfo[];
    /** The group's current status. */
    status: 'active' | 'ended';
}

const STATUS_POLL_INTERVAL_MS = 8000;
const HEARTBEAT_INTERVAL_MS = 15_000;
const COPIED_RESET_MS = 2000;

/**
 * Posts to a group-scoped endpoint with credentials included, ignoring the response body — used
 * for fire-and-forget actions (heartbeat, end) where only success/failure matters.
 *
 * @param url The endpoint URL.
 */
const postIgnoringBody = async (url: string): Promise<void> => {
    await fetch(url, {
        credentials: 'include',
        method: 'POST'
    }).catch(() => undefined);
};

/**
 * Hook für die Leiter-seitige Verwaltung einer Hörgruppe (Settings-Seite): Erstellen, Code
 * rotieren, Beenden, Mitglieder entfernen, und periodisches Abfragen der verbundenen Mitglieder.
 * Erzeugt außerdem clientseitig den QR-Code für den Join-Link, sobald eine Gruppe existiert.
 *
 * @returns Gruppen-State und die zugehörigen Aktionen.
 */
export const useGroupListening = () => {
    const {t} = useTranslation();
    const [group, setGroup] = useState<GroupSummary | null>(null);
    const [members, setMembers] = useState<GroupMemberInfo[]>([]);
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [copied, setCopied] = useState(false);

    /** Erstellt eine neue Hörgruppe (oder holt die bereits bestehende aktive Gruppe des Leiters). */
    const createGroup = useCallback(async () => {
        setIsCreating(true);

        try {
            const res = await fetch('/api/groups/create', {
                credentials: 'include',
                method: 'POST'
            });

            if (!res.ok) return;

            setGroup(await res.json() as GroupSummary);
        } finally {
            setIsCreating(false);
        }
    }, []);

    /** Beendet die aktuelle Gruppe. */
    const endGroup = useCallback(async () => {
        if (!group) return;

        await postIgnoringBody(`/api/groups/${group.groupId}/end`);
        setGroup(null);
        setMembers([]);
    }, [group]);

    /** Rotiert Join-Code und -Token der aktuellen Gruppe. */
    const rotateCode = useCallback(async () => {
        if (!group) return;

        const res = await fetch(`/api/groups/${group.groupId}/rotate-code`, {
            credentials: 'include',
            method: 'POST'
        });

        if (!res.ok) return;

        setGroup(await res.json() as GroupSummary);
    }, [group]);

    /**
     * Entfernt ein Mitglied aus der Gruppe. Aktualisiert die Liste optimistisch, bestätigt durch
     * den nächsten Status-Poll.
     *
     * @param memberId Die ID des zu entfernenden Mitglieds.
     */
    const kickMember = useCallback(async (memberId: string) => {
        if (!group) return;

        setMembers(current => current.filter(member => member.id !== memberId));
        await postIgnoringBody(`/api/groups/${group.groupId}/members/${memberId}/kick`);
    }, [group]);

    /**
     * Teilt den Join-Link über das native Share-Sheet auf unterstützten Geräten, oder kopiert ihn
     * sonst in die Zwischenablage und zeigt kurz eine Bestätigung.
     */
    const shareLink = useCallback(async () => {
        if (!group) return;

        // Web Share API ist in diesem TS-DOM-Lib nicht als optional typisiert, obwohl sie zur
        // Laufzeit nicht überall verfügbar ist – daher der explizite optionale Cast statt eines
        // direkten `navigator.share`-Zugriffs (ohne Intersection mit `Navigator`, da diese den
        // nicht-optionalen Typ sonst wieder erzwingen würde).
        const nav = navigator as {share?(data: {title?: string; url?: string}): Promise<void>};

        if (nav.share) {
            await nav.share({
                title: t('group.title'),
                url: group.joinUrl
            }).catch(() => undefined);

            return;
        }

        const copySucceeded = await navigator.clipboard.writeText(group.joinUrl).then(() => true, () => false);

        if (copySucceeded) setCopied(true);
    }, [group, t]);

    // Prüft beim Laden der Seite einmalig, ob der User bereits eine aktive Gruppe leitet (z.B. nach
    // einem Reload) – ohne diesen Check verschwindet eine aktive Gruppe fälschlich aus der Ansicht,
    // bis erneut auf "Hörgruppe erstellen" geklickt wird (das idempotente `create` holt sie dann
    // nur zufällig wieder hervor).
    useEffect(() => {
        let cancelled = false;

        /** Fragt die aktuell aktive Gruppe des Users ab, ohne dabei eine neue zu erstellen. */
        const checkExistingGroup = async () => {
            const res = await fetch('/api/groups/me', {credentials: 'include'});

            if (!res.ok) return;

            const existingGroup = await parseNullableJson<GroupSummary>(res);

            if (!cancelled && existingGroup) setGroup(existingGroup);
        };

        void checkExistingGroup();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!copied) return undefined;

        const timeout = setTimeout(() => setCopied(false), COPIED_RESET_MS);

        return () => clearTimeout(timeout);
    }, [copied]);

    useEffect(() => {
        if (!group) return undefined;

        let cancelled = false;

        /** Fragt den aktuellen Gruppen-Status ab und aktualisiert die Mitgliederliste. */
        const poll = async () => {
            const res = await fetch(`/api/groups/${group.groupId}/status`, {credentials: 'include'});

            if (!res.ok) return;

            const status = await res.json() as GroupStatusResponse;

            if (cancelled) return;

            if (status.status === 'ended') {
                setGroup(null);
                setMembers([]);

                return;
            }

            setMembers(status.members);
        };

        void poll();
        const interval = setInterval(() => {
            void poll();
        }, STATUS_POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [group]);

    // Hält die Leiter-Verbindung am Leben: periodischer Heartbeat, ein Screen-Wake-Lock gegen das
    // automatische Bildschirm-Sperren während des Guidens (ein gesperrtes Display drosselt/pausiert
    // `setInterval` auf vielen mobilen Browsern fast sofort, was Mitglieder sonst binnen Sekunden
    // fälschlich "Leiter getrennt" sehen lässt), und ein sofortiger Heartbeat beim Zurückkehren in den
    // Vordergrund – falls der Wake Lock nicht verfügbar war/abgelehnt wurde und die Verbindung
    // zwischenzeitlich trotzdem als getrennt erkannt wurde, wird sie so ohne Wartezeit wiederhergestellt.
    useEffect(() => {
        if (!group) return undefined;

        // Web Wake Lock API ist in diesem TS-DOM-Lib nicht vollständig typisiert; per Cast statt
        // direktem `navigator.wakeLock`-Zugriff, analog zum `share`-Cast oben.
        const nav = navigator as {wakeLock?: {request(type: 'screen'): Promise<{release(): Promise<void>}>}};
        let wakeLockSentinel: {release(): Promise<void>} | null = null;

        /** Fordert einen Screen-Wake-Lock an, sofern die API verfügbar ist und die Anfrage erlaubt wird. */
        const requestWakeLock = async () => {
            wakeLockSentinel = await nav.wakeLock?.request('screen').catch(() => null) ?? null;
        };

        /** Sendet einen Leiter-Heartbeat für die aktuelle Gruppe. */
        const sendHeartbeat = () => {
            void postIgnoringBody(`/api/groups/${group.groupId}/heartbeat`);
        };

        /**
         * Fordert den (vom Browser beim Verstecken automatisch freigegebenen) Wake Lock erneut an
         * und sendet sofort einen Heartbeat nach, sobald der Tab wieder sichtbar wird.
         */
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;

            void requestWakeLock();
            sendHeartbeat();
        };

        void requestWakeLock();
        sendHeartbeat();
        const interval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            void wakeLockSentinel?.release();
        };
    }, [group]);

    useEffect(() => {
        if (!group) {
            setQrDataUrl(null);

            return undefined;
        }

        let cancelled = false;

        /** Erzeugt den QR-Code für den Join-Link, sofern der Effekt inzwischen nicht bereinigt wurde. */
        const generateQrCode = async () => {
            const dataUrl = await QRCode.toDataURL(group.joinUrl).catch(() => null);

            if (!cancelled && dataUrl) setQrDataUrl(dataUrl);
        };

        void generateQrCode();

        return () => {
            cancelled = true;
        };
    }, [group]);

    return {
        canShare: typeof navigator !== 'undefined' && Boolean((navigator as {share?: unknown}).share),
        copied,
        createGroup,
        endGroup,
        group,
        isCreating,
        kickMember,
        members,
        qrDataUrl,
        rotateCode,
        shareLink,
        t
    };
};